import { Box, Typography } from "@mui/material";
import { useState } from "react";
import {
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import ButtonField from "./ButtonField";


interface MentionInputProps {
    expected: string;
    display: string;
    label: string;
    description: string;
    onValidated: () => void;
    primaryHex: string;
    fontFamily?: string;
}

const stripSurroundingQuotes = (s: string): string =>
    s.replace(/^[\s«"'‘’“”]+|[\s»"'‘’“”]+$/g, '').trim();

const MentionInput: React.FC<MentionInputProps> = ({ expected, display, label, description, onValidated, fontFamily }) => {
    const [value, setValue] = useState('');
    const [error, setError] = useState(false);
    const [validated, setValidated] = useState(false);

    const handleValidate = () => {
        const normalised = stripSurroundingQuotes(value.trim()).toLowerCase().replace(/\s+/g, ' ');
        const expectedNorm = expected.trim().toLowerCase().replace(/\s+/g, ' ');
        if (normalised === expectedNorm) {
            setValidated(true);
            setError(false);
            onValidated();
        } else {
            setError(true);
            setTimeout(() => { setValue(''); setError(false); }, 2000);
        }
    };

    if (validated) {
        return (
            <Box sx={{ p: 2.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 22, flexShrink: 0 }} />
                    <Box>
                        <Typography variant="subtitle2" fontWeight="700" color="#166534" sx={{ fontFamily }}>{label}</Typography>
                        <Typography sx={{
                            fontFamily: "'Caveat', cursive",
                            fontSize: '1.35rem',
                            fontWeight: 600,
                            color: '#1a3a1a',
                            mt: 0.5,
                            letterSpacing: '0.01em',
                            lineHeight: 1.3,
                        }}>
                            « {display} »
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: error ? '#fca5a5' : 'grey.300', borderRadius: 2, transition: 'border-color 0.2s' }}>
            <Typography variant="subtitle2" fontWeight="700" color="text.primary" sx={{ fontFamily, mb: 0.5 }}>{label}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily, mb: 1.5 }}>{description}</Typography>
            <Box sx={{ p: 2, bgcolor: 'white', border: '1px solid', borderColor: error ? '#fca5a5' : 'grey.300', borderRadius: 1.5, mb: 1.5 }}>
                <input
                    value={value}
                    onChange={e => { setValue(e.target.value); setError(false); }}
                    onKeyDown={e => e.key === 'Enter' && handleValidate()}
                    onPaste={e => e.preventDefault()}
                    type="text"
                    placeholder={`Tapez : « ${display} »`}
                    className="bg-transparent block w-full text-center text-base font-semibold border-b border-dashed border-gray-300 pb-2 focus:outline-none"
                    style={{ fontFamily, color: error ? '#dc2626' : '#111827' }}
                />
            </Box>
            {error && (
                <Typography variant="caption" color="error" sx={{ fontFamily, display: 'block', mb: 1, textAlign: 'center' }}>
                    Veuillez écrire exactement : « {display} »
                </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <ButtonField
                    label="Valider"
                    variant="contained"
                    className="font-bold"
                    onClick={handleValidate}
                    disabled={!value.trim()}
                />
            </Box>
        </Box>
    );
};

export default MentionInput;